import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { employeesApi } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Search, Settings, Wand2, Minus, Plus, Users, ZoomIn, ZoomOut, Maximize2, SlidersHorizontal, Trash2 } from "lucide-react";
import "./OrgStructurePage.css";

// Position enum → human label (mirrors backend EmployeePositionType)
const positionLabels = {
  project_manager: "Project Manager",
  art_director: "Art Director",
  "3d_artist": "3D Artist",
  animator: "Animator",
  designer: "Designer",
  developer: "Developer",
  ai_developer: "AI Developer",
  admin: "Administrator",
  accountant: "Accountant",
  hr: "HR",
  marketing: "Marketing",
  sales: "Sales Manager",
  support: "Support",
  finance_director: "Finance Director",
  other: "Other",
};

const NONE = "__none";

// node + all descendants — forbid picking them as a manager (would create a cycle)
const descendantIds = (id, childrenMap) => {
  const out = new Set([id]);
  const walk = (nid) => (childrenMap[nid] || []).forEach((c) => {
    if (!out.has(c.id)) { out.add(c.id); walk(c.id); }
  });
  walk(id);
  return out;
};

const NodeCard = ({ node, kids, isRoot, collapsed, onToggle, onOpen, query, dnd, variant, deptColors }) => {
  const hasKids = kids.length > 0;
  const match =
    query &&
    (node.name?.toLowerCase().includes(query) ||
      node.title?.toLowerCase().includes(query) ||
      positionLabels[node.position]?.toLowerCase().includes(query));
  const color = deptColors[node.department];

  const isDragging = dnd?.draggingId === node.id;
  const canDrop = !!dnd?.draggingId && dnd.draggingId !== node.id && !dnd.forbidden.has(node.id);
  const isDropOk = canDrop && dnd?.dropOkId === node.id;
  const title = node.title || positionLabels[node.position] || node.position;

  const dndProps = {
    draggable: !!dnd,
    onClick: () => onOpen(node),
    "data-testid": "org-node",
    title: "Drag onto a department or another person · click to edit",
    onDragStart: dnd ? (e) => dnd.onDragStart(e, node) : undefined,
    onDragEnd: dnd ? dnd.onDragEnd : undefined,
    onDragOver: canDrop ? (e) => { e.preventDefault(); dnd.setDropOk(node.id); } : undefined,
    onDragLeave: dnd ? () => { if (dnd.dropOkId === node.id) dnd.setDropOk(null); } : undefined,
    onDrop: canDrop ? (e) => { e.preventDefault(); dnd.onDropManager(e, node); dnd.setDropOk(null); } : undefined,
  };

  // compact list row — used inside a manager's leaf-panel
  if (variant === "row") {
    return (
      <div
        className={`leaf-row ${match ? "match" : ""} ${isDragging ? "is-dragging" : ""} ${isDropOk ? "drop-ok" : ""}`}
        style={color ? { "--dept": color } : undefined}
        {...dndProps}
      >
        <span className="lr-dot" />
        <span className="lr-name">{node.name}</span>
        <span className="lr-title">{title}</span>
      </div>
    );
  }

  return (
    <div
      className={`org-card ${isRoot ? "root" : ""} ${match ? "match" : ""} ${isDragging ? "is-dragging" : ""} ${isDropOk ? "drop-ok" : ""}`}
      style={color ? { "--dept": color } : undefined}
      {...dndProps}
    >
      <div className="oc-line">
        <span className="oc-dot" />
        <span className="oc-name">{node.name}</span>
      </div>
      <div className="oc-title">{title}</div>
      {hasKids && (
        <button
          type="button"
          className="org-toggle"
          onClick={(e) => { e.stopPropagation(); onToggle(node.id); }}
        >
          {collapsed ? <Plus className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
          {kids.length}
        </button>
      )}
    </div>
  );
};

const LeafPanel = ({ slug, people, deptColors, deptLabels, onOpen, query, dnd }) => (
  <div className="leaf-panel">
    <div className="leaf-panel__head" style={slug !== NONE ? { "--dept": deptColors[slug] } : undefined}>
      <span className="lp-dot" />
      {slug === NONE ? "No department" : (deptLabels[slug] || slug)}
      <span className="lp-count">{people.length}</span>
    </div>
    {people.map((k) => (
      <NodeCard
        key={k.id} node={k} kids={[]} isRoot={false} collapsed={false}
        onToggle={() => {}} onOpen={onOpen} query={query} dnd={dnd}
        deptColors={deptColors} variant="row"
      />
    ))}
  </div>
);

const TreeNode = ({ node, childrenMap, isRoot, expanded, onToggle, onOpen, query, dnd, deptColors, deptLabels, deptOrder }) => {
  const kids = childrenMap[node.id] || [];
  const collapsed = expanded[node.id] === false;
  const branchKids = kids.filter((k) => (childrenMap[k.id] || []).length > 0);
  const leafKids = kids.filter((k) => (childrenMap[k.id] || []).length === 0);

  // group leaf reports by department
  const byDept = {};
  leafKids.forEach((k) => { const d = k.department || NONE; (byDept[d] = byDept[d] || []).push(k); });
  const groupSlugs = [
    ...deptOrder.filter((s) => byDept[s]),
    ...Object.keys(byDept).filter((s) => s !== NONE && !deptOrder.includes(s)),
    ...(byDept[NONE] ? [NONE] : []),
  ];

  return (
    <div className="onode">
      <NodeCard
        node={node} kids={kids} isRoot={isRoot} collapsed={collapsed}
        onToggle={onToggle} onOpen={onOpen} query={query} dnd={dnd} deptColors={deptColors}
      />
      {kids.length > 0 && !collapsed && (
        // children wrap into a compact 2-column grid (grows down, not endlessly wide)
        <div className="onode-children">
          {/* CEO's direct reports render as peer cards; deeper managers group leaves into panels */}
          {isRoot
            ? leafKids.map((k) => (
                <NodeCard
                  key={k.id} node={k} kids={[]} isRoot={false} collapsed={false}
                  onToggle={() => {}} onOpen={onOpen} query={query} dnd={dnd} deptColors={deptColors}
                />
              ))
            : groupSlugs.map((slug) => (
                <LeafPanel
                  key={slug} slug={slug} people={byDept[slug]}
                  deptColors={deptColors} deptLabels={deptLabels} onOpen={onOpen} query={query} dnd={dnd}
                />
              ))}
          {branchKids.map((k) => (
            <TreeNode
              key={k.id} node={k} childrenMap={childrenMap} isRoot={false}
              expanded={expanded} onToggle={onToggle} onOpen={onOpen} query={query}
              dnd={dnd} deptColors={deptColors} deptLabels={deptLabels} deptOrder={deptOrder}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const OrgStructurePage = () => {
  const [nodes, setNodes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [autofilling, setAutofilling] = useState(false);
  const [editing, setEditing] = useState(null); // node being edited
  const [form, setForm] = useState({ title: "", department: "", manager_id: "" });
  const [saving, setSaving] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dropOkId, setDropOkId] = useState(null);
  const [hoverDept, setHoverDept] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [natH, setNatH] = useState(0);
  const [manageOpen, setManageOpen] = useState(false);
  const [newDept, setNewDept] = useState({ label: "", color: "#6366f1" });
  const viewportRef = useRef(null);
  const zoomRef = useRef(null);

  const deptColors = useMemo(() => Object.fromEntries(departments.map((d) => [d.id, d.color])), [departments]);
  const deptLabels = useMemo(() => Object.fromEntries(departments.map((d) => [d.id, d.label])), [departments]);
  const deptOrder = useMemo(() => departments.map((d) => d.id), [departments]);

  const fitToWidth = () => {
    const vp = viewportRef.current;
    const inner = zoomRef.current;
    if (!vp || !inner) return;
    const cw = inner.offsetWidth;
    const z = Math.min(1, (vp.clientWidth - 8) / Math.max(cw, 1));
    setZoom(z > 0 && isFinite(z) ? +z.toFixed(3) : 1);
  };

  const fetchData = async () => {
    try {
      const res = await employeesApi.getOrgStructure();
      setNodes(res.data.nodes || []);
    } catch (e) {
      console.error("Failed to load org structure", e);
      toast.error("Failed to load org structure");
    } finally {
      setLoading(false);
    }
  };
  const fetchDepartments = async () => {
    try {
      const res = await employeesApi.listDepartments();
      setDepartments(res.data || []);
    } catch (e) {
      console.error("Failed to load departments", e);
    }
  };

  useEffect(() => { fetchData(); fetchDepartments(); }, []);

  useLayoutEffect(() => {
    const h = zoomRef.current?.offsetHeight || 0;
    setNatH((prev) => (prev === h ? prev : h));
  });
  useLayoutEffect(() => { fitToWidth(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [nodes, departments]);
  useEffect(() => {
    const onResize = () => fitToWidth();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const { childrenMap, chartRoots, loose, sortedNodes } = useMemo(() => {
    const cm = {};
    nodes.forEach((n) => {
      if (n.manager_id) (cm[n.manager_id] = cm[n.manager_id] || []).push(n);
    });
    const byName = (a, b) => (a.name || "").localeCompare(b.name || "");
    Object.values(cm).forEach((arr) => arr.sort(byName));
    const roots = nodes.filter((n) => !n.manager_id);
    const chart = roots.filter((r) => (cm[r.id] || []).length > 0).sort(byName);
    const lo = roots.filter((r) => (cm[r.id] || []).length === 0).sort(byName);
    return { childrenMap: cm, chartRoots: chart, loose: lo, sortedNodes: [...nodes].sort(byName) };
  }, [nodes]);

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: p[id] === false ? true : false }));
  const collapseAll = () => {
    const all = {};
    nodes.forEach((n) => (childrenMap[n.id] ? (all[n.id] = false) : null));
    setExpanded(all);
  };
  const expandAll = () => setExpanded({});

  const openEditor = (node) => {
    setEditing(node);
    setForm({ title: node.title || "", department: node.department || "", manager_id: node.manager_id || "" });
  };

  const saveEditor = async () => {
    if (!editing) return;
    const patch = {};
    const t = form.title.trim();
    if (t !== (editing.title || "")) patch.title = t || null;
    if ((form.department || "") !== (editing.department || "")) patch.department = form.department || null;
    if ((form.manager_id || "") !== (editing.manager_id || "")) patch.manager_id = form.manager_id || null;
    if (Object.keys(patch).length === 0) { setEditing(null); return; }
    setSaving(true);
    try {
      await employeesApi.update(editing.id, patch);
      await fetchData();
      setEditing(null);
    } catch (e) {
      console.error("Save failed", e);
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleAutofill = async () => {
    setAutofilling(true);
    try {
      const res = await employeesApi.autofillDepartments(false);
      const n = res.data?.updated ?? 0;
      toast.success(n ? `Departments set: ${n}` : "All departments already filled");
      await fetchData();
    } catch (e) {
      console.error("Autofill failed", e);
      toast.error("Failed to fill departments");
    } finally {
      setAutofilling(false);
    }
  };

  // --- department catalog management ---
  const addDept = async () => {
    const label = newDept.label.trim();
    if (!label) return;
    try {
      await employeesApi.createDepartment({ label, color: newDept.color });
      setNewDept({ label: "", color: "#6366f1" });
      await fetchDepartments();
    } catch (e) { toast.error("Failed to add department"); }
  };
  const patchDept = async (id, patch) => {
    try { await employeesApi.updateDepartment(id, patch); await fetchDepartments(); }
    catch (e) { toast.error("Failed to update department"); }
  };
  const removeDept = async (d) => {
    if (!window.confirm(`Delete department “${d.label}”? People in it will become “No department”.`)) return;
    try {
      await employeesApi.deleteDepartment(d.id);
      await Promise.all([fetchDepartments(), fetchData()]);
    } catch (e) { toast.error("Failed to delete department"); }
  };

  // --- drag & drop ---
  const draggedForbidden = useMemo(
    () => (draggingId ? descendantIds(draggingId, childrenMap) : new Set()),
    [draggingId, childrenMap]
  );

  const assign = async (id, patch, msg) => {
    if (!id) return;
    try {
      await employeesApi.update(id, patch);
      await fetchData();
      if (msg) toast.success(msg);
    } catch (e) {
      console.error("DnD save failed", e);
      toast.error("Failed to save");
    }
  };

  const onDragStart = (e, node) => {
    setDraggingId(node.id);
    e.dataTransfer.setData("text/plain", node.id);
    e.dataTransfer.effectAllowed = "move";
    document.body.classList.add("org-dragging");
  };
  const onDragEnd = () => {
    setDraggingId(null);
    setDropOkId(null);
    setHoverDept(null);
    document.body.classList.remove("org-dragging");
  };
  const onDropManager = (e, target) => {
    const id = e.dataTransfer.getData("text/plain") || draggingId;
    if (!id || id === target.id) return;
    const dragged = nodes.find((n) => n.id === id);
    assign(id, { manager_id: target.id }, dragged ? `${dragged.name} → reports to ${target.name}` : null);
  };
  const onDropDept = (e, val) => {
    const id = e.dataTransfer.getData("text/plain") || draggingId;
    if (!id) return;
    const dragged = nodes.find((n) => n.id === id);
    assign(id, { department: val }, dragged ? `${dragged.name}: ${val ? deptLabels[val] : "no department"}` : null);
  };

  const dnd = { draggingId, forbidden: draggedForbidden, dropOkId, setDropOk: setDropOkId, onDragStart, onDragEnd, onDropManager };

  const query = search.trim().toLowerCase();
  const unassigned = nodes.filter((n) => !n.department).length;

  const managerOptions = useMemo(() => {
    if (!editing) return sortedNodes;
    const forbidden = descendantIds(editing.id, childrenMap);
    return sortedNodes.filter((n) => !forbidden.has(n.id));
  }, [editing, sortedNodes, childrenMap]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="org-structure-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Org Structure</h1>
          <p className="text-sm text-zinc-500">{nodes.length} people · click any card to edit</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" onClick={handleAutofill} disabled={autofilling}>
            <Wand2 className="h-4 w-4 mr-2" /> {autofilling ? "Filling…" : "Auto-fill departments by position"}
          </Button>
          <Button variant="outline" size="sm" className="border-zinc-800 text-zinc-300" onClick={expandAll}>Expand all</Button>
          <Button variant="outline" size="sm" className="border-zinc-800 text-zinc-300" onClick={collapseAll}>Collapse all</Button>
          <div className="flex items-center rounded-md border border-zinc-800 overflow-hidden">
            <button className="px-2 py-1.5 text-zinc-300 hover:bg-zinc-800" title="Zoom out" onClick={() => setZoom((z) => Math.max(0.3, +(z - 0.1).toFixed(2)))}><ZoomOut className="h-4 w-4" /></button>
            <span className="px-2 text-xs text-zinc-400 tabular-nums w-12 text-center">{Math.round(zoom * 100)}%</span>
            <button className="px-2 py-1.5 text-zinc-300 hover:bg-zinc-800" title="Zoom in" onClick={() => setZoom((z) => Math.min(1.5, +(z + 0.1).toFixed(2)))}><ZoomIn className="h-4 w-4" /></button>
            <button className="px-2 py-1.5 text-zinc-300 hover:bg-zinc-800 border-l border-zinc-800" title="Fit to width" onClick={fitToWidth}><Maximize2 className="h-4 w-4" /></button>
          </div>
          <Link to="/staff">
            <Button variant="outline" size="sm" className="border-zinc-800 text-zinc-300">
              <Settings className="h-4 w-4 mr-2" /> Staff
            </Button>
          </Link>
        </div>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or title…" className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-50" />
      </div>

      {unassigned > 0 && (
        <p className="text-xs text-zinc-500 mb-3">
          {unassigned} without a department — click “Auto-fill by position” or open a card to set it.
        </p>
      )}

      {/* drag & drop department zones */}
      <div className="dnd-bar">
        <div className="dnd-hint">
          Drag a card onto a <b>department</b> below — or drop it onto another person to set their <b>manager</b>.
        </div>
        <div className="dept-zones">
          {departments.map((d) => (
            <div
              key={d.id}
              className={`dept-zone ${hoverDept === d.id ? "over" : ""}`}
              style={{ "--dz": d.color, background: hoverDept === d.id ? `${d.color}22` : undefined }}
              onDragOver={(e) => { e.preventDefault(); setHoverDept(d.id); }}
              onDragLeave={() => setHoverDept((h) => (h === d.id ? null : h))}
              onDrop={(e) => { e.preventDefault(); onDropDept(e, d.id); setHoverDept(null); }}
            >
              <span className="dz-dot" />
              {d.label}
            </div>
          ))}
          <div
            className={`dept-zone clear ${hoverDept === "__clear" ? "over" : ""}`}
            style={{ background: hoverDept === "__clear" ? "rgba(63,63,70,.2)" : undefined }}
            onDragOver={(e) => { e.preventDefault(); setHoverDept("__clear"); }}
            onDragLeave={() => setHoverDept((h) => (h === "__clear" ? null : h))}
            onDrop={(e) => { e.preventDefault(); onDropDept(e, null); setHoverDept(null); }}
          >
            <span className="dz-dot" />
            no department
          </div>
          <button type="button" className="dept-zone dept-zone--manage" onClick={() => setManageOpen(true)}>
            <SlidersHorizontal className="h-3.5 w-3.5" /> Manage
          </button>
        </div>
      </div>

      {chartRoots.length === 0 && loose.length > 0 && (
        <p className="text-sm text-zinc-500 mb-4">
          No reporting lines yet. Drag a person onto another person (or open a card and set its <span className="text-zinc-300">Manager</span>) to start building the chart top-down.
        </p>
      )}

      {/* the chart */}
      {chartRoots.length > 0 && (
        <div className="org-viewport" ref={viewportRef}>
          <div className="org-zoom" ref={zoomRef} style={{ transform: `scale(${zoom})`, transformOrigin: "top center", marginBottom: natH ? -(natH * (1 - zoom)) : 0 }}>
            {chartRoots.map((r) => (
              <div className="org-tree" key={r.id}>
                <TreeNode
                  node={r} childrenMap={childrenMap} isRoot expanded={expanded}
                  onToggle={toggle} onOpen={openEditor} query={query} dnd={dnd}
                  deptColors={deptColors} deptLabels={deptLabels} deptOrder={deptOrder}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* people without a manager yet */}
      {loose.length > 0 && (
        <div className="org-loose">
          <div className="org-loose-head">
            <Users className="h-3.5 w-3.5" /> Not placed yet · {loose.length} (click to assign a manager)
          </div>
          <div className="org-loose-grid">
            {loose.map((n) => (
              <NodeCard key={n.id} node={n} kids={[]} isRoot={false} collapsed={false} onToggle={() => {}} onOpen={openEditor} query={query} dnd={dnd} deptColors={deptColors} />
            ))}
          </div>
        </div>
      )}

      {/* editor dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-['Manrope']">{editing?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-zinc-300">Job Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={editing ? (positionLabels[editing.position] || "e.g. Head of 3D") : ""} className="bg-zinc-950 border-zinc-800 text-zinc-50" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Department</Label>
              <Select value={form.department || "none"} onValueChange={(v) => setForm({ ...form, department: v === "none" ? "" : v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50"><SelectValue placeholder="— none —" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="none" className="text-zinc-400">— none —</SelectItem>
                  {departments.map((d) => (
                    <SelectItem key={d.id} value={d.id} className="text-zinc-300">{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Manager (reports to)</Label>
              <Select value={form.manager_id || "none"} onValueChange={(v) => setForm({ ...form, manager_id: v === "none" ? "" : v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50"><SelectValue placeholder="— no manager (top level) —" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 max-h-72">
                  <SelectItem value="none" className="text-zinc-400">— no manager (top level) —</SelectItem>
                  {managerOptions.map((m) => (
                    <SelectItem key={m.id} value={m.id} className="text-zinc-300">{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={saveEditor} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* manage departments dialog */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-['Manrope']">Departments</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2 max-h-[50vh] overflow-y-auto pr-1">
            {departments.map((d) => (
              <div key={d.id} className="flex items-center gap-2">
                <input type="color" value={d.color || "#52525b"} onChange={(e) => patchDept(d.id, { color: e.target.value })} className="h-8 w-8 rounded bg-transparent border border-zinc-700 cursor-pointer" title="Color" />
                <Input defaultValue={d.label} key={d.id + ":" + d.label} onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== d.label) patchDept(d.id, { label: v }); }} className="bg-zinc-950 border-zinc-800 text-zinc-50 flex-1" />
                <button type="button" className="p-2 text-zinc-500 hover:text-red-400" title="Delete" onClick={() => removeDept(d)}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-800">
            <input type="color" value={newDept.color} onChange={(e) => setNewDept({ ...newDept, color: e.target.value })} className="h-8 w-8 rounded bg-transparent border border-zinc-700 cursor-pointer" title="Color" />
            <Input value={newDept.label} onChange={(e) => setNewDept({ ...newDept, label: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") addDept(); }} placeholder="New department name" className="bg-zinc-950 border-zinc-800 text-zinc-50 flex-1" />
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={addDept} disabled={!newDept.label.trim()}>
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrgStructurePage;
